import { useMemo } from "react";
import { useGetList, type Identifier } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import {
  buildThreads,
  type ThreadNoteRow,
} from "@/modules/threads/threadModel";

/** Whose threads a view shows: people (contact notes) and projects. */
export type ThreadScope = {
  contactIds: Identifier[];
  dealIds: Identifier[];
};

const inFilter = (field: string, ids: Identifier[]) => ({
  [`${field}@in`]: `(${ids.join(",")})`,
});

const LIST = { page: 1, perPage: 200 } as const;
const BY_DATE = { field: "date", order: "DESC" } as const;

/** Threads (roots + replies) of contacts and projects, merged. */
export const useRecordThreads = (scope: ThreadScope) => {
  const hasContacts = scope.contactIds.length > 0;
  const hasDeals = scope.dealIds.length > 0;

  const contactRoots = useGetList<ThreadNoteRow & { id: Identifier }>(
    "contact_notes",
    {
      filter: {
        ...inFilter("contact_id", scope.contactIds),
        "parent_id@is": null,
      },
      pagination: LIST,
      sort: BY_DATE,
    },
    { enabled: hasContacts, staleTime: 15_000 },
  );
  const dealRoots = useGetList<ThreadNoteRow & { id: Identifier }>(
    "deal_notes",
    {
      filter: { ...inFilter("deal_id", scope.dealIds), "parent_id@is": null },
      pagination: LIST,
      sort: BY_DATE,
    },
    { enabled: hasDeals, staleTime: 15_000 },
  );

  const contactRootIds = (contactRoots.data ?? []).map((row) => row.id);
  const dealRootIds = (dealRoots.data ?? []).map((row) => row.id);

  const contactReplies = useGetList<ThreadNoteRow & { id: Identifier }>(
    "contact_notes",
    {
      filter: inFilter("parent_id", contactRootIds),
      pagination: { page: 1, perPage: 500 },
      sort: BY_DATE,
    },
    { enabled: contactRootIds.length > 0, staleTime: 15_000 },
  );
  const dealReplies = useGetList<ThreadNoteRow & { id: Identifier }>(
    "deal_notes",
    {
      filter: inFilter("parent_id", dealRootIds),
      pagination: { page: 1, perPage: 500 },
      sort: BY_DATE,
    },
    { enabled: dealRootIds.length > 0, staleTime: 15_000 },
  );

  const threads = useMemo(
    () =>
      buildThreads({
        contactRoots: hasContacts ? (contactRoots.data ?? []) : [],
        dealRoots: hasDeals ? (dealRoots.data ?? []) : [],
        contactReplies: contactRootIds.length
          ? (contactReplies.data ?? [])
          : [],
        dealReplies: dealRootIds.length ? (dealReplies.data ?? []) : [],
      }),
    [
      hasContacts,
      hasDeals,
      contactRoots.data,
      dealRoots.data,
      contactReplies.data,
      dealReplies.data,
      contactRootIds.length,
      dealRootIds.length,
    ],
  );

  const isPending =
    (hasContacts && contactRoots.isPending) ||
    (hasDeals && dealRoots.isPending);

  return { threads, isPending };
};

/** Refresh threads and the activity timeline after a change. */
export const useInvalidateThreads = () => {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["contact_notes"] }),
      queryClient.invalidateQueries({ queryKey: ["deal_notes"] }),
      queryClient.invalidateQueries({ queryKey: ["client_activity_timeline"] }),
      queryClient.invalidateQueries({ queryKey: ["deal_activity_unified"] }),
    ]);
};
